import { useEffect, useState } from 'react'
import { SeasonalRateSummary } from '@/types'
import { fetchAuthenticatedJSONResult } from '@/utils/fetch/fetchAuthenticated'
import { logEvent } from '@/logging'

type Result = {
  seasonalRateSummary: SeasonalRateSummary[] | null
  isLoading: boolean
}

/**
 * Fetches the seasonal (month × hour × day-type × tier) rate summary for an
 * arbitrary plan id — the user's own plan or any non-custom searchable plan —
 * so a plan's rates can be previewed in `DailyTOURateOverview` before it is
 * saved as the selected plan. Returns null while no plan is selected or on error.
 *
 * Loading is derived from which plan the loaded data is for, so `isLoading`
 * flips to true synchronously the moment `planId` changes (no render gap where
 * it briefly reads false). Lets a page reliably gate on it during initial load.
 */
export function usePlanSeasonalRateSummary(planId: number | undefined): Result {
  const [loaded, setLoaded] = useState<{
    planId: number | undefined
    data: SeasonalRateSummary[] | null
  }>({ planId: undefined, data: null })

  useEffect(() => {
    if (planId == null) {
      setLoaded({ planId: undefined, data: null })
      return
    }

    let cancelled = false
    fetchAuthenticatedJSONResult<SeasonalRateSummary[]>(
      `/plans/${planId}/seasonal_rate_summary`,
    ).then((result) => {
      if (cancelled) {
        return
      }
      if (result.isErr()) {
        logEvent(
          `Failed to fetch seasonal rate summary for plan ${planId}: ${result.error.status}`,
        )
        setLoaded({ planId, data: null })
        return
      }
      setLoaded({ planId, data: result.value })
    })

    return () => {
      cancelled = true
    }
  }, [planId])

  // Loading until the loaded data corresponds to the currently-requested plan.
  const isLoading = planId != null && loaded.planId !== planId
  const seasonalRateSummary = loaded.planId === planId ? loaded.data : null

  return { seasonalRateSummary, isLoading }
}
